import React, { useState } from 'react'
import Box from 'react-styled-box';
import Select, { components } from 'react-select';
import _get from 'lodash/get';
import useToggle from 'UseToggle.js';
import {translation, device, browser} from 'utilsHelper.js';
import RegisterByMyIdAttributes from 'registerByMyIdAttributes.enum.js';

export const prepareUserData = (customerData) => {
    const data = _get(customerData, 'data') || customerData;
    if (!data || !data.attributes) return null;

    const { attributes, id } = data;
    
    const getAttrValue = (key) => {
        const item = attributes.find(a => a.attribute === key);
        return item ? item.value : '';
    };
    
    return {
        identifier: id,
        firstName: getAttrValue(RegisterByMyIdAttributes.FIRST_NAME),
        lastName: getAttrValue(RegisterByMyIdAttributes.LAST_NAME),
        birthDate: getAttrValue(RegisterByMyIdAttributes.DATE_OF_BIRTH),
        pesel: getAttrValue(RegisterByMyIdAttributes.PERSONAL_IDENTIFIER),
        docType: getAttrValue(RegisterByMyIdAttributes.DOCUMENT_TYPE),
        docNumber: getAttrValue(RegisterByMyIdAttributes.DOCUMENT_NUMBER),
        docExpiry: getAttrValue(RegisterByMyIdAttributes.DOCUMENT_EXPIRY_DATE),
        countryOfBirth: getAttrValue(RegisterByMyIdAttributes.COUNTRY_OF_BIRTH),
        accountNumber: getAttrValue(RegisterByMyIdAttributes.BANK_ACCOUNT_NUMBER),
        nationality: getAttrValue(RegisterByMyIdAttributes.NATIONALITY),
        city: getAttrValue(RegisterByMyIdAttributes.ADDRESS_CITY),
        street: getAttrValue(RegisterByMyIdAttributes.ADDRESS_STREET),
        houseNumber: getAttrValue(RegisterByMyIdAttributes.ADDRESS_NUMBER),
        postalCode: getAttrValue(RegisterByMyIdAttributes.POSTAL_CODE),
        country: getAttrValue(RegisterByMyIdAttributes.ADDRESS_COUNTRY_NAME)
    };
};

export const prepareRegistrationPayload = (values, myAffiliateToken, btag, clickId, promoCode) => {
    const fullAddress = `${values.street || ''} ${values.houseNumber || ''}`.trim();

    const sowConsents = (values.sourceOfFunds || []).map(id => ({
        container: "SOW",
        definitionId: id,
        accepted: true
    }));

    let communicationConsents = [];
    if (values.marketingConsent) {
        communicationConsents = [
            { "container": "EMAIL", "accepted": true },
            { "container": "SMS", "accepted": true }
        ];
    } else {
        communicationConsents = [
            { "container": "EMAIL", "accepted": false },
            { "container": "SMS", "accepted": false }
        ];
    }

    const rawPayload = {
        channel: "desktop",
        browser: browser(),
        device: device(),
        firstName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: values.birthDate,
        personalIdentificationNumber: values.pesel,
        documentType: values.docType,
        documentNumber: values.docNumber,
        documentExpirationDate: values.docExpiry,
        nationality: values.nationality,
        countryCode: values.countryOfBirth,
        country: values.country,
        bankAccountNumber: (values.accountNumber || '').replace(/[^\d]/g, ''),
        city: values.city,
        address: fullAddress,
        postalCode: values.postalCode,
        email: values.email,
        password: values.password,
        mobile: values.mobile,
        identifier: values.identifier,
        verificationCode: values.smsCode || null,
        consents: [
            {
                "container": "GIIF",
                "accepted": !!values.giifDeclaration
            },
            {
                "container": "PEP",
                "accepted": !!values.pepDeclaration
            },
            {
                "container": "LOTTERY_PARTICIPATION", 
                "accepted": false 
            },
            {
                "container": "REGULATIONS",
                "accepted": !!values.ageConsent
            },
            {
                "container": "OFFERS",
                "definitionId": 9,
                "accepted": !!values.bonus
            },
            ...communicationConsents,
            ...sowConsents
        ],
        btag: btag,
        clickId: clickId,
        promoCode: promoCode,
        affiliateToken: myAffiliateToken
    };

    const payload = Object.keys(rawPayload).reduce((acc, key) => {
        if (rawPayload[key] !== undefined) {
            acc[key] = rawPayload[key];
        }
        return acc;
    }, {});

    return payload;
};

export const handleChatClick = (e) => {
    if (e) e.preventDefault();
        
    if (window.chatButton && typeof window.chatButton.onClick === 'function') {
        window.chatButton.onClick();
    }
};

export const renderField = ({ input, label, type, fullWidth, disabled, S, meta: { touched, error, valid } }) => {
    const hasValidationError = !!error;
    const isSuccess = (valid && !!input.value) || disabled;

    return (
        <S.InputWrapper fullWidth={fullWidth}>
            {isSuccess && <S.FloatingLabel>{label}</S.FloatingLabel>}
            <S.Input 
                {...input} 
                placeholder={isSuccess ? "" : label} 
                type={type} 
                hasError={hasValidationError} 
                isValid={isSuccess}
                disabled={disabled}
                autoComplete="off"
            />
            {isSuccess && (
                <S.CheckIcon style={{ position: 'absolute', right: '12px', top: '10px' }}>
                    ✓
                </S.CheckIcon>
            )}
            {touched && error && <S.ErrorText>{translation(error[0])}</S.ErrorText>}
        </S.InputWrapper>
    );
};

export const renderPasswordField = ({ input, label, S, meta: { error, valid, touched } }) => {
    const [showPassword, togglePassword] = useToggle(false);
    const hasValidationError = !!error;
    const isSuccess = valid && !!input.value;

    return (
        <S.InputWrapper>
            {isSuccess && <S.FloatingLabel>{label}</S.FloatingLabel>}
            <S.Input 
                {...input} 
                placeholder={isSuccess ? "" : label} 
                type={showPassword ? 'text' : 'password'} 
                hasError={hasValidationError}
                isValid={isSuccess}
                autoComplete="new-password"
            />
            <S.PasswordToggler type="button" onClick={togglePassword}>
                <S.PasswordTogglerIcon isActive={showPassword} className="fa fa-eye" />
            </S.PasswordToggler>
            {touched && error && <S.ErrorText>{translation(error[0])}</S.ErrorText>}
        </S.InputWrapper>
    );
};

export const renderCheckboxField = ({ input, label, S, id, meta: { touched, error } }) => {
    const isChecked = !!input.value;
    const uniqueId = id || `cb-${input.name}`;

    return (
        <Box flexDirection="column" width="100%">
            <S.CheckboxWrapper>
                <S.HiddenInput 
                    {...input} 
                    type="checkbox" 
                    id={uniqueId}
                    checked={isChecked} 
                />
                <S.FakeCheckbox htmlFor={uniqueId} checked={isChecked}>
                    {isChecked && <S.CheckMark>✔</S.CheckMark>}
                </S.FakeCheckbox>
                <S.Label htmlFor={uniqueId}>{label}</S.Label>
            </S.CheckboxWrapper>
            {touched && error && ( <S.ErrorText>{translation(error[0])}</S.ErrorText>)}
        </Box>
    );
};

export const renderCheckboxWithSpoiler = (props) => {
    const { input, labelPart1, labelPart2, S, meta: { touched, error }, moreText, lessText } = props;
    const isChecked = !!input.value;
    const uniqueId = `cb-step3-${input.name}`;

    return (
        <S.CheckboxRow>
            <S.CheckboxWrapper>
                <S.HiddenInput 
                    {...input} 
                    type="checkbox" 
                    id={uniqueId} 
                    checked={isChecked}
                />
                <S.FakeCheckbox checked={isChecked} htmlFor={uniqueId}>
                    {isChecked && <S.CheckMark>✔</S.CheckMark>}
                </S.FakeCheckbox>
            </S.CheckboxWrapper>
            
            <Box flexDirection="column" style={{ flex: 1 }}>
                <S.Label htmlFor={uniqueId}>
                    <S.LabelPart>{labelPart1}</S.LabelPart>
                    {labelPart2 && (
                        <S.Details>
                            <S.Summary moreText={moreText} lessText={lessText} />
                            <S.LabelPart>{labelPart2}</S.LabelPart>
                        </S.Details>
                    )}
                </S.Label>
                {touched && error && (
                    <S.ErrorText style={{ marginTop: '4px' }}>
                        {translation(error[0])}
                    </S.ErrorText>
                )}
            </Box>
        </S.CheckboxRow>
    );
};

const Option = props => {
    const { label } = props.data;
    const { S } = props.selectProps;

    return (
        <components.Option {...props}>
            <Box flexDirection="row" alignItems="center">
                <S.FakeCheckbox checked={props.isSelected}>
                    {props.isSelected && <S.CheckMark>✔</S.CheckMark>}
                </S.FakeCheckbox>
                <S.SelectLabel>{label}</S.SelectLabel>
            </Box>
        </components.Option>
    );
};

export const renderMultiSelectDropdown = ({ input, options, S, placeholder, meta: { touched, error } }) => {
    const formattedOptions = options.map(opt => ({
        value: opt.definitionId,
        label: opt.name
    }));

    const selectedOptions = formattedOptions.filter(opt => 
        (input.value || []).includes(opt.value)
    );

    const hasValue = selectedOptions.length > 0;
    const customBorderColor = hasValue ? '#cfcfcf' : '#D32F2F';

    const handleSelectChange = (selectedList) => {
        const values = selectedList ? selectedList.map(item => item.value) : [];
        input.onChange(values);
    };

    const customSelectStyles = {
        placeholder: (provided) => ({
        ...provided,
        fontSize: '14px',
        }),
        control: (provided, state) => ({
            ...provided,
            minHeight: '40px',
            borderRadius: '4px',
            backgroundColor: '#fff',
            border: `1px solid ${customBorderColor}`, 
            boxShadow: 'none', 
            '&:hover': {
                borderColor: customBorderColor 
            }
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            margin: '2px',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#000',
            fontSize: '13px',
            padding: '3px 6px',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#666',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#e0e0e0',
                color: '#000',
            },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: '#fff',
            color: '#000',
            cursor: 'pointer',
            padding: '8px 12px',
            '&:hover': { 
                backgroundColor: '#f5f5f5' 
            },
            backgroundColor: state.isFocused ? '#f5f5f5' : '#fff',
            color: '#000'
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        menu: (provided) => ({ ...provided, zIndex: 100 })
    };

    return (
        <S.DropdownContainer>
            <Select
                {...input}
                options={formattedOptions}
                value={selectedOptions}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option }}
                styles={customSelectStyles}
                placeholder={placeholder}
                onChange={handleSelectChange}
                onBlur={() => input.onBlur()}
                S={S}
            />
            {touched && error && <S.ErrorText>{translation(error[0])}</S.ErrorText>}
        </S.DropdownContainer>
    );
};